import * as React from 'react'
import { Provider as ReduxProvider } from 'react-redux'
import {
  getWidgetProps,
  WPSClient,
  Args,
  withArgs,
  Widget,
} from 'DVWebNodeUtils'
import { requestWpsData } from '../../utils/requestWpsData'
import { StringBundle } from '../../components/localizer/localizer'

import { configureStore } from '../../store'
import { IDetailPageState } from '../../reducers'
import { Collections } from '../../components/collection/collections'
import {
  normalizeGetDetailPage,
  normalizeGetPreferences,
  normalizeGetStrings,
  NormalizeGetDetailPageType,
  NormalizeGetPreferencesType,
  NormalizeGetStringsType,
} from '../../data/normalize'
import { getBtfInput } from '../../data/getDetailPageInputs'
import { safeBoolean } from '../../utils/utils'
import { PurchaseFlowWrapper } from '../../components/purchaseFlowWrapper/purchaseFlowWrapper'
import { ErrorBoundary } from '../../components/error/ErrorBoundary'
import { blockedPageSelector } from '../../selectors/component/pageBlockComponentSelectors'
import { DPPlaybackArea } from '../../utils/DPPlaybackArea'

export interface BtfProps {
  state:
    | NormalizeGetDetailPageType &
        NormalizeGetPreferencesType &
        NormalizeGetStringsType &
        Pick<IDetailPageState, 'features'>
    | Pick<IDetailPageState, 'features' | 'pageTitleId'>
}

export const getProps: getWidgetProps<BtfProps> = async (
  {
    context,
    titleID,
    useMFA,
    usePlayer,
    isMovie,
    isElcano,
    useEpisodeListSortingT1,
    useEpisodeListSortingT2,
    useNodeGiftCard,
    useNodeDeeplinking,
    useNewHover,
  },
  api
) => {
  const features = {
    useMFA: safeBoolean(useMFA),
    usePlayer: safeBoolean(usePlayer),
    useEpisodeListSortingT1: safeBoolean(useEpisodeListSortingT1),
    useEpisodeListSortingT2: safeBoolean(useEpisodeListSortingT2),
    useNodeGiftCard: safeBoolean(useNodeGiftCard),
    useNodeDeeplinking: safeBoolean(useNodeDeeplinking),
    useNewHover: safeBoolean(useNewHover),
  }
  if (safeBoolean(isMovie)) {
    return {
      state: {
        features,
        pageTitleId: titleID,
      },
    }
  }
  const operation = (wpsClient: WPSClient) =>
    wpsClient.GetDetailPage(getBtfInput({ context, titleID, isElcano: safeBoolean(isElcano)},
      features.useEpisodeListSortingT1 || features.useEpisodeListSortingT2))

  const reply = await requestWpsData({
    context,
    api,
    operation,
    stringBundle: StringBundle.BTF,
    requestPreferences: true
  })
  return {
    state: {
      features,
      ...normalizeGetDetailPage(titleID, reply.operationResponse),
      ...normalizeGetPreferences(reply.preferencesResponse!),
      ...normalizeGetStrings(reply.stringsResponse!)
    },
  }
}

const BtfWidget = (props: BtfProps & Args) => {
  const { logger, context: requestContext, state } = props
  const store = configureStore({ ...state, requestContext }, logger)

  // Page block prevents any render in btf. Message will be shown in atf
  const pageBlock = blockedPageSelector(store.getState())
  if (pageBlock) {
    return null
  }

  return (
    <ReduxProvider store={store}>
      <ErrorBoundary name='btf'>
        <DPPlaybackArea>
          <Collections />
        </DPPlaybackArea>
        <PurchaseFlowWrapper />
      </ErrorBoundary>
    </ReduxProvider>
  )
}

export const Btf: Widget<BtfProps> = {
  name: 'detail-btf',
  getProps: global.__SERVER__ === true ? getProps : undefined,
  Component: withArgs(BtfWidget) as React.ComponentType<any>,
}
